import React, { useState, useMemo } from "react";
import { decode } from "html-entities";
import PropTypes from "prop-types";
import ProductForm from "./ProductForm";
import he from "he";

// Helper: Parse HTML or text to key-value pairs
const parseSpecifications = (details) => {
  if (!details) return [];

  // Try to parse as HTML table rows
  if (/<tr/i.test(details)) {
    const rows = [];
    const rowRegex = /<tr[^>]*>(.*?)<\/tr>/gi;
    let match;
    while ((match = rowRegex.exec(details))) {
      const cells = match[1]
        .split(/<\/td>/i)
        .map((cell) => decode(cell.replace(/<[^>]+>/g, "").trim()))
        .filter(Boolean);
      if (cells.length >= 2) rows.push({ key: cells[0], value: cells[1] });
    }
    if (rows.length) return rows;
  }

  // Fallback: parse as key: value lines
  return details
    .split(/<br\s*\/?>|\n/)
    .map((line) => {
      const [key, ...rest] = line.split(/:|：/);
      if (rest.length) {
        return {
          key: decode(key.trim()),
          value: decode(rest.join(":").trim()),
        };
      }
      return null;
    })
    .filter(Boolean);
};

const ProductDetailTabs = ({
  product,
  locationsData,
  queryParams,
  country,
  Webfeed,
}) => {
  const [activeTab, setActiveTab] = useState("form");

  const isOutOfStock = useMemo(() => {
    return (
      product.stock?.toLowerCase() !== "in stock" ||
      !product.quantity ||
      product.quantity <= 0 ||
      !product.display_price ||
      typeof product.display_price !== "number"
    );
  }, [product.stock, product.quantity, product.display_price]);
  const decodedDetails = useMemo(
    () => (product?.details ? he.decode(product.details) : ""),
    [product?.details]
  );

  const specRows = useMemo(
    () => (decodedDetails ? parseSpecifications(decodedDetails) : []),
    [decodedDetails]
  );


  const { descriptionRow, tableRows } = useMemo(() => {
    const descriptionRowIndex = specRows.findIndex(
      (row) => row.key.toLowerCase() === "description"
    );
    const descriptionRow =
      descriptionRowIndex !== -1 ? specRows[descriptionRowIndex] : null;
    const tableRows =
      descriptionRowIndex !== -1
        ? specRows.slice(0, descriptionRowIndex)
        : specRows;

    return { descriptionRow, tableRows };
  }, [specRows]);

  const renderTabContent = () => {
    if (activeTab === "form") {
      if (isOutOfStock) {
        return (
          <div className="text-center fs-4 fw-semibold text-danger p-4 bg-danger-subtle border rounded">
            Sorry, this product is currently out of stock.
          </div>
        );
      }

      return (
        <ProductForm
          product={product}
          locationsList={locationsData}
          queryParams={queryParams}
          country={country}
          Webfeed={Webfeed}
        />
      );
    }

    return (
      <div>
        {product.name && (
          <div className="w-100 pt-3 pb-3 border-top">
            <h2 className="fs-4 fw-semibold text-dark mb-3">Product Details</h2>
            <div
              className="small text-secondary"
              dangerouslySetInnerHTML={{ __html: product.name }}
            />
          </div>
        )}

        {decodedDetails && (
          <div className="w-100 border-top pt-3 mb-4">
            <h2 className="fs-4 fw-semibold text-dark mb-3">Specifications</h2>
            {tableRows.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped responsive-font">
                  <tbody>
                    {tableRows.map((row, i) => (
                      <tr key={`${row.key}-${i}`} style={{ border: "none" }}>
                        <td
                          className="px-3 py-2 fw-medium text-secondary"
                          style={{ width: "33.333%", border: "none" }}
                        >
                          {row.key}
                        </td>
                        <td className="px-3 py-2 text-dark" style={{ border: "none" }}>
                          {row.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            ) : (
              <div
                className="small text-secondary"
                dangerouslySetInnerHTML={{ __html: decodedDetails }}
              />
            )}

            {descriptionRow && (
              <div className="pt-2">
                <h2 className="fs-4 fw-semibold text-dark mb-3">Description</h2>
                <div
                  className="small text-secondary"
                  dangerouslySetInnerHTML={{ __html: descriptionRow.value }}
                />
              </div>
            )}
          </div>
        )
        }
      </div >
    );
  };

  return (
    <div className="w-100 bg-white rounded-4 shadow-sm border overflow-hidden pt-3">
      <div className="d-flex mx-3 p-1 bg-light rounded-3 responsive-font">
        <div className="flex-grow-1 px-1">
          <button
            className={`w-100 py-2 fw-semibold border-0 
        ${activeTab === "form" ? "bg-dark text-white rounded-3" : "bg-light text-dark"} `}
            onClick={() => setActiveTab("form")}
          >
            Place An Order Request
          </button>
        </div>
        <div className="flex-grow-1 px-1">
          <button
            className={`w-100 py-2 fw-semibold border-0 
        ${activeTab === "description" ? "bg-dark text-white rounded-3" : "bg-light text-dark"} `}
            onClick={() => setActiveTab("description")}
          >
            Product Description
          </button>
        </div>
      </div>

      <div className="px-4 pt-2 pb-4 px-md-4">{renderTabContent()}</div>
    </div>
  );
};

ProductDetailTabs.propTypes = {
  product: PropTypes.shape({
    stock: PropTypes.string,
    quantity: PropTypes.number,
    display_price: PropTypes.string,
    name: PropTypes.string,
    details: PropTypes.string,
  }).isRequired,
  locationsData: PropTypes.array.isRequired,
  queryParams: PropTypes.object.isRequired,
  country: PropTypes.string.isRequired,
};

export default ProductDetailTabs;